import { useEffect, useState } from 'react';
import { useRevalidator } from 'react-router';

import { Button } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, Select } from '@ds/forms';
import { moeda } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { descricaoDoGateway } from '@/data/gateways';
import { dados } from '@/data/porta';
import type { Fatura } from '@/data/tipos';
import { CampoDeMotivo, centavosDoTexto, motivoValido } from './apoioDoPainel';

function mensagem(e: unknown): string {
    return e instanceof Error ? e.message : 'Tente de novo.';
}

function daquiA(dias: number): string {
    const d = new Date();
    d.setDate(d.getDate() + dias);
    return d.toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
}

/**
 * Fatura avulsa: implantação, treinamento, o que não é mensalidade.
 *
 * É REGISTRO. A plataforma não tem boleto, PIX nem cartão ligados, e o modal diz
 * isso antes de alguém achar que a clínica recebeu uma cobrança.
 */
export function FaturaAvulsaModal({ aberto, onFechar, clinicas }: {
    aberto: boolean;
    onFechar: () => void;
    /** Uma só: a clínica vem fixa, da ficha. */
    clinicas: { id: string; nome: string }[];
}) {
    const { revalidate } = useRevalidator();
    const aviso = useAviso();
    const [clinicaId, setClinicaId] = useState(clinicas[0]?.id ?? '');
    const [descricao, setDescricao] = useState('');
    const [valor, setValor] = useState('');
    const [vencimento, setVencimento] = useState(daquiA(10));
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    useEffect(() => {
        if (aberto) {
            setClinicaId(clinicas[0]?.id ?? '');
            setDescricao('');
            setValor('');
            setVencimento(daquiA(10));
            setErro(null);
        }
    }, [aberto, clinicas]);

    const centavos = centavosDoTexto(valor);
    const valido = Boolean(clinicaId) && descricao.trim().length >= 3 && centavos !== null && centavos > 0 && Boolean(vencimento);

    const emitir = async () => {
        if (centavos === null) return;
        setSalvando(true);
        setErro(null);
        try {
            const fatura = await dados().emitirFaturaAvulsa({ clinicaId, descricao, valorCentavos: centavos, vencimento });
            /* A fatura já existe; a cobrança online é o passo seguinte, e a falha
               dela (sem gateway ativo, gateway fora do ar) não desfaz a fatura —
               vai dita no aviso, e a fatura fica com "Cobrar" na tabela. */
            let cobranca: string;
            try {
                const c = await dados().criarCobranca(fatura.id);
                cobranca = `cobrança no ${descricaoDoGateway(c.gateway).nome}`;
            } catch (e) {
                cobranca = `sem cobrança online: ${mensagem(e)}`;
            }
            aviso({ title: 'Fatura avulsa registrada', description: `${fatura.clinica} · ${moeda(fatura.valorCentavos)} · ${cobranca}` });
            onFechar();
            void revalidate();
        } catch (e) {
            setErro(mensagem(e));
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open={aberto} onClose={onFechar} width={520} title="Fatura avulsa"
            description="Uma cobrança fora da mensalidade, registrada na clínica."
            dirty={descricao.trim().length > 0 || valor.trim().length > 0}
            footer={(
                <>
                    <Button variant="secondary" onClick={onFechar}>Cancelar</Button>
                    <Button loading={salvando} disabled={!valido} onClick={() => { void emitir(); }}>Registrar fatura</Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                <Alert tone="neutral" dismissible={false} title="Nada é enviado à clínica">
                    Sem gateway de pagamento, a fatura é o registro do que se combinou. O pagamento entra aqui por
                    baixa manual, quando chegar.
                </Alert>
                {clinicas.length > 1 && (
                    <FormField label="Clínica" htmlFor="avulsa-clinica" required>
                        <Select
                            id="avulsa-clinica" value={clinicaId} onChange={setClinicaId}
                            options={clinicas.map((c) => ({ value: c.id, label: c.nome }))}
                        />
                    </FormField>
                )}
                <FormField label="Do que é" htmlFor="avulsa-descricao" required hint="Ex.: implantação, treinamento da equipe.">
                    <Input id="avulsa-descricao" value={descricao} onChange={setDescricao} />
                </FormField>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 14 }}>
                    <FormField label="Valor" htmlFor="avulsa-valor" required hint="Em reais.">
                        <Input id="avulsa-valor" value={valor} onChange={setValor} placeholder="0,00" />
                    </FormField>
                    <FormField label="Vencimento" htmlFor="avulsa-vencimento" required>
                        <Input id="avulsa-vencimento" type="date" value={vencimento} onChange={setVencimento} />
                    </FormField>
                </div>
            </div>
        </Modal>
    );
}

/**
 * Estorno de fatura paga.
 *
 * Registro de que o valor foi devolvido POR FORA. Não há gateway por onde o
 * dinheiro volte sozinho — e dizer "estornado em até 7 dias", como o protótipo,
 * seria prometer um movimento que ninguém faz.
 */
export function EstornoModal({ fatura, onFechar }: { fatura: Fatura | null; onFechar: () => void }) {
    const { revalidate } = useRevalidator();
    const aviso = useAviso();
    const [motivo, setMotivo] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    useEffect(() => {
        if (fatura) {
            setMotivo('');
            setErro(null);
        }
    }, [fatura]);

    const estornar = async () => {
        if (!fatura) return;
        setSalvando(true);
        setErro(null);
        try {
            await dados().estornarFatura(fatura.id, motivo);
            aviso({ title: 'Estorno registrado', description: `${fatura.clinica} · ${fatura.competencia} · ${moeda(fatura.valorCentavos)}` });
            onFechar();
            void revalidate();
        } catch (e) {
            setErro(mensagem(e));
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open={fatura !== null} onClose={onFechar} width={500} tone="danger" title="Registrar estorno?"
            description={fatura ? `${fatura.clinica} · ${fatura.competencia} · ${moeda(fatura.valorCentavos)}` : ''}
            dirty={motivo.trim().length > 0}
            footer={(
                <>
                    <Button variant="secondary" onClick={onFechar}>Cancelar</Button>
                    <Button variant="danger" loading={salvando} disabled={!motivoValido(motivo)} onClick={() => { void estornar(); }}>
                        Registrar estorno
                    </Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                <Alert tone="warning" dismissible={false} title="O dinheiro não volta sozinho">
                    Sem gateway, o estorno registra que o valor foi devolvido por fora — transferência, PIX, o que
                    se combinou. Faça a devolução antes ou logo depois de registrar.
                </Alert>
                <CampoDeMotivo id="estorno-motivo" valor={motivo} onChange={setMotivo} />
            </div>
        </Modal>
    );
}
